/*  iceberg-ui  */
import { Icon, WithContext } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Button, Paper } from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import {
    AdminBackButton,
    PartnerBackButton
} from 'application/Shared/Components/Buttons/BackButton';
import { TagChipCell } from 'application/Shared/Components/Cells';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { TABLENAMES } from 'application/Shared/Constants';
import { AddTagModal } from 'application/Shared/Modals/Application/globalTag/AddTagModal';

/* Hooks */
import {
    useFetchGlobalTags,
    useFetchTagLinks,
    useFetchTagSettings
} from 'hooks/dataAccess/GlobalTag';

/*  React  */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
    TagEditorTableContext,
    TagEditorTableContextProvider,
    useTagEditorTableContext
} from './TagEditorContext';

const AddTagButton = () => {
    const { t } = useTranslation();
    const { setOpenAddTagModal } = useTagEditorTableContext();

    return (
        <Box
            sx={{
                position: 'absolute',
                right: '16px',
                top: '20px'
            }}
        >
            <Button
                onClick={() => setOpenAddTagModal(true)}
                variant="contained"
                color="primary"
                data-trackid="admin_tagEditor_createNewTag"
            >
                {t('TagEditor.create_new_tag')}
            </Button>
        </Box>
    );
};

const ActionsCell = ({ row }) => (
    <WithContext context={TagEditorTableContext}>
        {({ setEditingTagId, t }) => (
            <Box display="flex" alignItems="center" justifyContent="flex-end">
                <KebabMenu
                    options={[
                        {
                            key: 'edit',
                            label: t('Common.edit'),
                            value: 'edit'
                        }
                    ]}
                    onClick={(value) => {
                        if (value === 'edit') {
                            setEditingTagId(row.original.id);
                        }
                    }}
                    data-testid="tagEditorActions"
                />
            </Box>
        )}
    </WithContext>
);

// TODO: Move this to a shared utility function since this is basically copied from TagsPicker.
// This one has added fields, but that's probably fine. We can have the function just return the payload object,
// then let the useEffect it's called in call the necessary 'setState' functions.
const makePayload = ({
    globalTags,
    setLoaded,
    setTags,
    setTagsMap,
    tagLinks,
    tagSettings
}) => {
    /*
        Based on loading all auvikManaged ('default') global tags, all of the tenant's custom tag setting,
        and any tag links they've created, we compile a list to provide them with any tag they should be able to assign.

        We normalize them into objects of:
        {
            id (globalTag's ID)
            color (set on the tagSetting, with globalTag's defaultColor as fallback)
            name (set on the globalTag)
        }

        We build the list with the following priority
        tenant's tagSettings > tenant's tagLinks > globalTags


        if we want to leverage description at some point, we could always add that here.
    */
    const tagObjectsByGlobalTagId = {};

    tagSettings.forEach((tagSetting) => {
        if (!tagObjectsByGlobalTagId[tagSetting.globalTagId]) {
            tagObjectsByGlobalTagId[tagSetting.globalTagId] = {
                auvikManaged: !!tagSetting?.globalTag?.auvikManaged,
                color: tagSetting.color,
                defaultColor: tagSetting?.globalTag?.defaultColor,
                defaultDescription: tagSetting?.globalTag?.defaultDescription,
                description: tagSetting.description,
                id: tagSetting?.globalTag?.id,
                name: tagSetting?.globalTag?.name
            };
        }
    });

    tagLinks.forEach((tagLink) => {
        if (!tagObjectsByGlobalTagId[tagLink.globalTagId]) {
            tagObjectsByGlobalTagId[tagLink.globalTagId] = {
                auvikManaged: !!tagLink?.globalTag?.auvikManaged,
                defaultColor: tagLink?.globalTag?.defaultColor,
                defaultDescription: tagLink?.globalTag?.defaultDescription,
                id: tagLink?.globalTag?.id,
                name: tagLink?.globalTag?.name
            };
        }
    });

    globalTags.forEach((tag) => {
        if (!tagObjectsByGlobalTagId[tag.id]) {
            tagObjectsByGlobalTagId[tag.id] = {
                auvikManaged: !!tag.auvikManaged,
                defaultColor: tag.defaultColor,
                defaultDescription: tag.defaultDescription,
                id: tag.id,
                name: tag.name
            };
        }
    });

    const deduplicatedTags = Object.values(tagObjectsByGlobalTagId);

    setTags(deduplicatedTags);
    setTagsMap(tagObjectsByGlobalTagId);
    setLoaded(true);
};

const columns = [
    {
        accessorKey: 'name',
        cell: TagChipCell,
        enableColumnFilter: true,
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'name',
        name: 'Common.name',
        size: 200,
        testId: 'name'
    },
    {
        accessorKey: 'description',
        cell: ({ row }) =>
            row.original.description || row.original.defaultDescription,
        enableColumnFilter: false,
        header: Header,
        id: 'description',
        name: 'Common.description',
        size: 500,
        testId: 'description'
    },
    {
        accessorKey: 'auvikManaged',
        cell: ({ cell }) => cell.getValue() && <Icon src="Check" />,
        filterFn: 'equalsString',
        header: Header,
        id: 'auvikManaged',
        meta: {
            filter: {
                control: 'Radio',
                defaultOptions: [{ options: ['true', 'false'] }]
            }
        },
        name: 'Common.default',
        size: 50,
        testId: 'auvikManaged'
    },
    {
        accessorKey: 'actions',
        cell: ActionsCell,
        enableColumnFilter: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        meta: {
            align: 'right',
            header: { justifyContent: 'flex-end' }
        },
        name: 'Common.actions',
        size: 50,
        testId: 'actions'
    }
];

export const TagEditorPage = () => {
    const { t } = useTranslation();

    const {
        openAddTagModal,
        setOpenAddTagModal,
        setEditingTagId,
        editingTagId
    } = useTagEditorTableContext();

    const { clientId, partnerId } = useParams();
    const platformTenantId = clientId || partnerId;

    const [loaded, setLoaded] = useState(false);
    const [tags, setTags] = useState([]);

    const [tagsMap, setTagsMap] = useState({});

    const globalTagsParams = useMemo(
        () => ({
            auvikManaged: true,
            size: -1
        }),
        []
    );

    const {
        globalTags,
        loading: globalTagsLoading,
        refetchData: refetchGlobalTags
    } = useFetchGlobalTags(globalTagsParams);

    const tagLinkParams = useMemo(
        () => ({
            includeTag: true,
            mergeClientAndPartner: true,
            platformTenantId,
            size: -1
        }),
        [platformTenantId]
    );

    const {
        tagLinks,
        loading: tagLinksLoading,
        refetchData: refetchTagLinks
    } = useFetchTagLinks(tagLinkParams);

    const tagSettingsParams = useMemo(
        () => ({
            includeTag: true,
            mergeClientAndPartner: true,
            platformTenantId,
            size: -1
        }),
        [platformTenantId]
    );

    const {
        tagSettings,
        loading: tagSettingsLoading,
        refetchData: refetchTagSettings
    } = useFetchTagSettings(tagSettingsParams);

    useEffect(() => {
        if (globalTagsLoading && tagLinksLoading && tagSettingsLoading) return;

        makePayload({
            globalTags,
            setLoaded,
            setTags,
            setTagsMap,
            tagLinks,
            tagSettings
        });
    }, [
        globalTags,
        tagLinks,
        tagSettings,
        globalTagsLoading,
        tagLinksLoading,
        tagSettingsLoading
    ]);

    if (!loaded) {
        return null;
    }

    const onSuccessFn = () => {
        refetchGlobalTags();
        refetchTagLinks();
        refetchTagSettings();
    };

    const tagBeingEdited = editingTagId && tagsMap[editingTagId];

    return (
        <>
            {/*
                TODO: Handle this link for global admin
            */}
            {clientId ? (
                <AdminBackButton linkTo="/configure" />
            ) : (
                <PartnerBackButton linkTo="/configure" />
            )}

            <ManagedTableV2ContextProvider
                columns={columns}
                getRowId="id"
                mode="client"
                ignoreSearchParams
                rows={tags}
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                sortDirection="ASC"
                sortKey="name"
                tableName={TABLENAMES.TAG_EDITOR}
            >
                <PageTitle title={t('TagEditor.title')} />
                <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{ mt: '24px', p: 0, position: 'relative' }}
                >
                    <AddTagButton />
                    {openAddTagModal && (
                        <AddTagModal
                            onClose={() => setOpenAddTagModal(false)}
                            onSuccessFn={onSuccessFn}
                            open={openAddTagModal}
                        />
                    )}

                    {editingTagId && tagBeingEdited && (
                        <AddTagModal
                            onClose={() => setEditingTagId(null)}
                            onSuccessFn={onSuccessFn}
                            open={!!editingTagId}
                            tagDefaultValues={{
                                color:
                                    tagBeingEdited.color ||
                                    tagBeingEdited.defaultColor,
                                description:
                                    tagBeingEdited.description ||
                                    tagBeingEdited.defaultDescription,
                                name: tagBeingEdited.name
                            }}
                        />
                    )}

                    <ManagedTableV2UI renderFooter />
                </Paper>
            </ManagedTableV2ContextProvider>
        </>
    );
};

export const TagEditor = () => (
    <TagEditorTableContextProvider>
        <TagEditorPage />
    </TagEditorTableContextProvider>
);
